/**
 * Formant preservation: shift the notes without shifting the singer.
 *
 * A pitch shift multiplies every frequency by the same factor. That is correct
 * for the harmonics, which are what carries the note, and wrong for the
 * **formants** — the resonances of the body producing the sound, which are a
 * property of the throat, not of the pitch being sung. Move those up an octave
 * too and the singer sounds physically smaller. This is the "chipmunk" of a
 * naive shifter, and it is the whole reason this module exists.
 *
 * The fix is to separate the spectral envelope — the slow shape of the spectrum,
 * which is where the formants live — from the harmonic structure inside it, and
 * to move only the second.
 *
 * ## The correction happens before the shift, not after
 *
 * The obvious arrangement is to shift, measure how far the envelope moved, and
 * move it back. This does the equivalent thing one step earlier, and it is
 * strictly better.
 *
 * Write `E(u)` for the envelope of the source at frequency `u`, `f` for the pitch
 * shift factor, and `g` for how far the formants should move on their own.
 * Resampling by `f` sends a component at `u` to `u·f`, so the output's envelope
 * is `E'(v/f)` where the pre-warped source's is `E'`. Wanting the formants at
 * `E(v/g)` means `E'(v/f) = E(v/g)`, that is `E'(u) = E(u·f/g)`. So the filter to
 * apply first is
 *
 *     G(u) = E(u·f/g) / E(u)
 *
 * and only the **ratio** `f/g` reaches this module, which is why it takes one
 * number rather than two. Three cases are worth naming:
 *
 *     f/g = 1     the formants go wherever the pitch goes — no filter at all,
 *                 which is what a shifter does if left alone
 *     g = 1       they stay exactly where they were: `G(u) = E(u·f)/E(u)`
 *     f = 1       they move on their own while the note does not, which is what
 *                 makes a voice sound like a different person singing it
 *
 * The filter is computable from the source frame alone. Three things follow, and
 * all three are why it is done this way round:
 *
 * - **No alignment problem.** Correcting afterwards means pairing each output
 *   frame with the source frame it came from, and WSOLA has by then nudged the
 *   read position by up to five milliseconds to find a waveform match. Here
 *   there is nothing to pair: the filter and the audio are the same frame.
 * - **Half the analysis.** One envelope per frame instead of one for the source
 *   and one for the result.
 * - **It composes with everything already there.** The output of this is
 *   ordinary audio, so the existing resample-then-stretch pipeline is unchanged
 *   and every property it has — exact length, flat level, aligned channels — is
 *   still its own to keep.
 *
 * ## The envelope is cepstral
 *
 * A log magnitude spectrum is a slow shape (the formants) with a fast ripple on
 * top (the harmonics, one bump per multiple of the pitch). Separating them is
 * the same problem as separating a slow signal from a fast one, so the same tool
 * works: transform the log spectrum, keep the low end, transform back. The
 * transform of a log spectrum is the **cepstrum**, and truncating it is
 * **liftering**.
 *
 * Nothing here needs to know the pitch, which is the point. Estimating F0 to
 * decide what to smooth would put a pitch detector — and its failures — on the
 * path of every transposed clip.
 */

import { fft, isPowerOfTwo } from '@/util/fft';

/**
 * The narrowest spectral feature the envelope keeps, in Hz.
 *
 * Everything faster than this is treated as harmonic ripple and smoothed away.
 * Formants are hundreds of Hz wide and around a thousand apart, so 700Hz keeps
 * their shape while removing the harmonics of any voice pitched below it.
 *
 * Above that it stops working, and the reason is not a bug to be fixed: a voice
 * singing above 700Hz has harmonics spaced further apart than its formants are
 * wide, so there is no longer a slow shape and a fast ripple to separate. Every
 * cepstral envelope has this limit. See the module's limitations in
 * docs/PITCH_SHIFTING.md.
 */
const ENVELOPE_DETAIL_HZ = 700;

/** Analysis frame, in samples. 2048 at 48kHz is 43ms and 23Hz a bin. */
const FRAME = 2048;

/**
 * Four frames overlap at any moment. A spectrum that is modified and transformed
 * back no longer fits its window exactly, so the result is windowed again on the
 * way out; two Hann windows need a quarter-frame hop to add back up smoothly.
 */
const HOP = FRAME / 4;

/** How far the correction may push any one bin, as a linear gain. 20dB either way. */
const MAX_CORRECTION = 10;

function hann(length: number): Float64Array {
  const w = new Float64Array(length);
  for (let i = 0; i < length; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / length);
  return w;
}

/**
 * The smooth spectral envelope of one frame's log magnitude spectrum.
 *
 * `logMag` is the full length-N log magnitude — symmetric, because the frame is
 * real — and the result is its cepstrally smoothed version, in the same units.
 * `keep` is how many cepstral coefficients survive either side of zero.
 *
 * The transform is real and symmetric in both directions, so the imaginary parts
 * are carried only because the FFT is complex; they come back as rounding noise
 * and are dropped.
 */
export function liftered(logMag: Float64Array, keep: number): Float64Array {
  const n = logMag.length;
  const re = Float64Array.from(logMag);
  const im = new Float64Array(n);

  fft(re, im, true);
  // Keep the low quefrencies — the slow shape — and discard the ripple. The
  // cepstrum is symmetric, so the tail belongs to the low end as much as the
  // head does and has to be kept with it.
  for (let q = keep + 1; q < n - keep; q++) {
    re[q] = 0;
    im[q] = 0;
  }
  fft(re, im, false);
  return re;
}

/**
 * The correction filter for one frame: `E(u·warp) / E(u)`, per bin.
 *
 * Reading past Nyquist is clamped to the top bin rather than wrapped. Wrapping
 * would fold the bottom of the spectrum onto the top and invent a resonance
 * there; clamping simply says the envelope stops, which is what it does.
 */
function correctionFor(logEnvelope: Float64Array, factor: number, bins: number): Float64Array {
  const gains = new Float64Array(bins);
  const top = bins - 1;
  for (let k = 0; k < bins; k++) {
    const source = Math.min(top, k * factor);
    const low = Math.floor(source);
    const high = Math.min(top, low + 1);
    const t = source - low;
    const wanted = logEnvelope[low] * (1 - t) + logEnvelope[high] * t;
    const gain = Math.exp(wanted - logEnvelope[k]);
    gains[k] = Math.min(MAX_CORRECTION, Math.max(1 / MAX_CORRECTION, gain));
  }
  return gains;
}

/**
 * Pre-warps `channels` so that a later pitch shift leaves the formants where
 * `warp` asks for them.
 *
 * `warp` is `f/g` — the pitch factor over the formant factor. One means the
 * formants are to go wherever the pitch goes, and there is nothing to do.
 *
 * The filter is derived from the **mono sum** and applied identically to every
 * channel, for the reason WSOLA shares its alignment: a correction computed per
 * channel would apply two different filters to the two sides of a stereo image
 * and pull it apart. The formants of a stereo recording are the same formants.
 *
 * Fragments shorter than one analysis frame are returned untouched. There is no
 * envelope to estimate in less than a frame, and the shift itself already falls
 * back to plain resampling at that length.
 */
export function preWarpFormants(
  channels: readonly Float32Array[],
  sampleRate: number,
  warp: number,
): Float32Array[] {
  if (channels.length === 0) return [];
  const length = channels[0]?.length ?? 0;
  if (length < FRAME || !isPowerOfTwo(FRAME) || Math.abs(warp - 1) < 1e-9) {
    return channels.map((c) => new Float32Array(c));
  }

  const bins = FRAME / 2 + 1;
  const keep = Math.max(2, Math.round(sampleRate / ENVELOPE_DETAIL_HZ));
  const window = hann(FRAME);

  const mono = monoOf(channels);
  const out = channels.map(() => new Float64Array(length));
  // Accumulated w², so the overlap-add is normalised by the window energy that
  // actually landed on each sample. A constant would be right in the middle and
  // wrong at both ends, where fewer frames overlap — which is exactly the fade
  // in and out that the stretcher had to compensate for separately.
  const norm = new Float64Array(length);

  const re = new Float64Array(FRAME);
  const im = new Float64Array(FRAME);
  const logMag = new Float64Array(FRAME);
  const channelRe = new Float64Array(FRAME);
  const channelIm = new Float64Array(FRAME);

  for (let start = 0; start + FRAME <= length; start += HOP) {
    // The envelope, from the mono sum.
    for (let i = 0; i < FRAME; i++) {
      re[i] = mono[start + i] * window[i];
      im[i] = 0;
    }
    fft(re, im, false);
    for (let i = 0; i < FRAME; i++) {
      // A floor rather than an epsilon added to the magnitude: it puts a real
      // limit on the dynamic range the envelope models, so a silent bin cannot
      // become an arbitrarily large negative number and drag the cepstrum with
      // it. -200dB is far below anything audible.
      logMag[i] = Math.log(Math.max(1e-10, Math.hypot(re[i], im[i])));
    }
    const envelope = liftered(logMag, keep);
    const gains = correctionFor(envelope, warp, bins);

    for (let c = 0; c < channels.length; c++) {
      const source = channels[c];
      for (let i = 0; i < FRAME; i++) {
        channelRe[i] = source[start + i] * window[i];
        channelIm[i] = 0;
      }
      fft(channelRe, channelIm, false);
      for (let k = 0; k < bins; k++) {
        const gain = gains[k];
        channelRe[k] *= gain;
        channelIm[k] *= gain;
        // The negative half mirrors the positive one, and has to keep doing so
        // or the inverse transform is not real.
        const mirror = FRAME - k;
        if (mirror < FRAME && mirror !== k) {
          channelRe[mirror] *= gain;
          channelIm[mirror] *= gain;
        }
      }
      fft(channelRe, channelIm, true);

      const destination = out[c];
      for (let i = 0; i < FRAME; i++) destination[start + i] += channelRe[i] * window[i];
    }
    for (let i = 0; i < FRAME; i++) norm[start + i] += window[i] * window[i];
  }

  return out.map((values, c) => {
    const result = new Float32Array(length);
    for (let i = 0; i < length; i++) {
      // Below this the window energy is too small to divide by meaningfully:
      // only the last partial frame's tail reaches it, and the source is the
      // honest answer there rather than a number multiplied by a thousand.
      result[i] = norm[i] > 1e-3 ? values[i] / norm[i] : channels[c][i];
    }
    return result;
  });
}

/** Channels summed to mono, used only to estimate the envelope. */
function monoOf(channels: readonly Float32Array[]): Float32Array {
  if (channels.length === 1) return channels[0];
  const length = channels[0].length;
  const mono = new Float32Array(length);
  for (const channel of channels) {
    for (let i = 0; i < length; i++) mono[i] += channel[i];
  }
  const scale = 1 / channels.length;
  for (let i = 0; i < length; i++) mono[i] *= scale;
  return mono;
}
